'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Loader2,
  ChevronLeft,
  Check,
  ClipboardList,
  FileText,
  Globe,
  PencilRuler,
  Banknote,
} from 'lucide-react';
import { GenerationUsageMeter, PackUsageNotice } from '@/features/subscription';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { cn } from '@/shared/lib/cn';
import { SettingsCard, SettingsItem } from '@/shared/ui/settings';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import {
  useConceptAlbumSettings,
  useAlbum,
  useAlbumCostPrediction,
  useAlbumEstimatePicker,
  isPremiumAlbumTemplate,
  MATERIALS_SECTION_TOGGLE_ROW,
  SECONDARY_SECTION_TOGGLE_ROWS,
  AlbumEstimateOptions,
  AlbumRenderThumbnailStrip,
  AlbumDuplicateDialog,
  useAlbumDuplicatePrompt,
  type AlbumCostPredictionParams,
  type AlbumLanguage,
  type AlbumMeasurementSystem,
  type AlbumSectionToggleRow,
  type AlbumTemplate,
} from '@/features/design/album';
import { sortAlbumLocalesForUser, getLocaleByCode } from '@/shared/constants';
import { useJobQueueStore } from '@/features/design/job-queue';
import { useRendersForConcept } from '@/features/design/render/model/renders-cache';
import { RenderStatus } from '@/shared/constants';
import { isUserVisibleRenderType } from '@/types/render';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { useHasCompletedMainRender } from '@/features/design/concept';
import { useProject } from '@/features/project/hooks/use-projects';
import type { ConceptAngle } from '@/types/concept';

interface AlbumTabContentProps {
  conceptId: string;
  /** Project the concept belongs to — required to list the concept's estimates
   *  for the album Estimate picker (FE4). */
  projectId: string;
  conceptAngles: ConceptAngle[];
  isArchived?: boolean;
}

type AlbumPanelView =
  | 'main'
  | 'template'
  | 'language'
  | 'requirements'
  | 'estimate'
  | 'units';

// Render order for the template (design) axis. `standard` first (free), then
// the premium (Pro-tier) templates. Mirrors the Apple/Android catalog order.
const ALBUM_TEMPLATE_OPTIONS: readonly AlbumTemplate[] = [
  'standard',
  'architect',
  'studio',
];

// Units picker order — Auto (server decides, default) first, then the two
// explicit systems. Mirrors Apple/Android.
const ALBUM_MEASUREMENT_SYSTEM_OPTIONS: readonly AlbumMeasurementSystem[] = [
  'auto',
  'metric',
  'imperial',
];

export function AlbumTabContent({
  conceptId,
  projectId,
  conceptAngles,
  isArchived = false,
}: AlbumTabContentProps) {
  const tAlbum = useTranslations('album');
  const tCommon = useTranslations('common');
  const tEstimate = useTranslations('common.estimate');
  // Album settings from store
  const albumSettings = useConceptAlbumSettings(conceptId);
  const {
    selectedTemplate,
    includeBlueprints,
    includeMaterials,
    includePreparationGuide,
    includeSafetyRequirements,
    includeBuildingRegulations,
    measurementSystem,
    selectedLanguage: storedSelectedLanguage,
    requirements: storedRequirements,
    updateSetting,
  } = albumSettings;

  // Apple parity (`AlbumPanelContent`): the panel swaps its content area
  // between `main` / `language` / `requirements` rather than overlaying a
  // nested sheet on top of the settings. The Generate button stays anchored
  // to the bottom across all three views.
  const [view, setView] = useState<AlbumPanelView>('main');
  // Sorted once when the language view opens — stable during selection.
  const [languageOrder, setLanguageOrder] = useState(() =>
    sortAlbumLocalesForUser(storedSelectedLanguage)
  );

  const selectedLanguageName =
    getLocaleByCode(storedSelectedLanguage)?.name ?? storedSelectedLanguage;

  const openLanguageView = () => {
    setLanguageOrder(sortAlbumLocalesForUser(storedSelectedLanguage));
    setView('language');
  };

  // Cost-estimate picker (FE4) — this design's estimates first (default),
  // "New estimate", then sibling designs' estimates greyed at the end. All
  // defaulting/stale-guard logic lives in the shared hook.
  const estimatePicker = useAlbumEstimatePicker(projectId, conceptId);
  const {
    currentDesignEstimates,
    otherDesignEstimates,
    selectedEstimateId,
    setSelectedEstimateId,
    isEstimateIncluded,
    excludeEstimate,
  } = estimatePicker;
  const selectedEstimate = [
    ...currentDesignEstimates,
    ...otherDesignEstimates,
  ].find((e) => e.id === selectedEstimateId);
  // Row value for the Estimate setting: the localized document name when one
  // is picked (estimates carry no user-set title — Apple/Android row-value
  // parity), "New estimate", or "Don't include".
  const estimateRowValue = !isEstimateIncluded
    ? tEstimate('album.dontInclude')
    : selectedEstimate
      ? tEstimate('card.untitled')
      : tEstimate('album.generateNew');

  // Album generation hook
  const {
    generateAlbum,
    isGenerating: isGeneratingAlbum,
    lastGenerateResult,
    resetGenerateResult,
  } = useAlbum({
    conceptId,
  });

  // Duplicate-album prompt — shared with the single-column panel so the
  // "already exists" confirmation appears in the two-column layout too.
  const duplicatePrompt = useAlbumDuplicatePrompt(
    lastGenerateResult,
    resetGenerateResult
  );

  // Check for album in queue
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const albumInQueue = jobQueue.find(
    (item) => item.conceptId === conceptId && item.jobType === 'album'
  );
  const hasAlbumInQueue =
    albumInQueue &&
    (albumInQueue.status === RenderStatus.PENDING ||
      albumInQueue.status === RenderStatus.PROCESSING);
  const isAlbumGenerating = isGeneratingAlbum || hasAlbumInQueue;

  // Renders owned by TanStack Query (`cacheKeys.renders.list(conceptId)`).
  const rendersForConcept = useRendersForConcept(conceptId);
  const selectedRenderIdsFromStore = useConceptStore(
    (state) => state.ui.selectedRenderId
  );

  // Calculate selected renders for album generation
  const selectedRendersForAlbum = useMemo(() => {
    const result: Record<string, string> = {};
    conceptAngles.forEach((ca) => {
      const completedRenders = rendersForConcept
        .filter(
          (r) =>
            r.relationships?.concept_angle?.data?.id === ca.id &&
            r.attributes.status === RenderStatus.COMPLETED &&
            isUserVisibleRenderType(r.attributes.render_type)
        )
        .sort(
          (a, b) =>
            new Date(a.attributes.created_at).getTime() -
            new Date(b.attributes.created_at).getTime()
        );
      if (completedRenders.length > 0) {
        const selectedRenderId = selectedRenderIdsFromStore[ca.id];
        const selectedRender = selectedRenderId
          ? completedRenders.find((r) => r.id === selectedRenderId) ||
            completedRenders[completedRenders.length - 1]
          : completedRenders[completedRenders.length - 1];
        if (selectedRender) {
          result[ca.id] = selectedRender.id;
        }
      }
    });
    return result;
  }, [conceptAngles, rendersForConcept, selectedRenderIdsFromStore]);

  // Thumbnail URLs for the exact renders the album will use — resolved from the
  // selected-render map so the strip matches `selected_renders` 1:1.
  const selectedRenderUrls = useMemo(() => {
    return Object.values(selectedRendersForAlbum)
      .map((renderId) => {
        const render = rendersForConcept.find((r) => r.id === renderId);
        return (
          render?.relationships?.file?.meta?.url ?? render?.attributes.file_url
        );
      })
      .filter((url): url is string => Boolean(url));
  }, [selectedRendersForAlbum, rendersForConcept]);

  const renderedCount = Object.keys(selectedRendersForAlbum).length;
  const totalCount = conceptAngles.length;
  const hasPartialRenders = renderedCount > 0 && renderedCount < totalCount;
  const useSecondaryVariant = totalCount > 1 && hasPartialRenders;

  // Server-predicted token cost for the CURRENT settings — shown as a suffix
  // on the Generate button. The cost-relevant params mirror the generate
  // request below 1:1 (memoized so the hook's debounce is stable).
  const costPredictionParams = useMemo<AlbumCostPredictionParams>(
    () => ({
      renders_count: renderedCount,
      include_materials: includeMaterials,
      include_blueprints: includeBlueprints,
      include_preparation_guide: includePreparationGuide,
      include_safety_requirements: includeSafetyRequirements,
      include_building_regulations: includeBuildingRegulations,
      // True only for the "New estimate" picker option — exactly when the
      // generate request spreads `generate_new_estimate: true` (see
      // useAlbumEstimatePicker.estimatePayload).
      generate_new_estimate:
        'generate_new_estimate' in estimatePicker.estimatePayload,
    }),
    [
      renderedCount,
      includeMaterials,
      includeBlueprints,
      includePreparationGuide,
      includeSafetyRequirements,
      includeBuildingRegulations,
      estimatePicker.estimatePayload,
    ]
  );
  const {
    tokens: predictedTokens,
    isSignificantCost,
    warningMessage: costWarningMessage,
  } = useAlbumCostPrediction(conceptId, costPredictionParams);

  // Token-cost chrome (≈N suffix + walnut, token-worded cost warning) is
  // per-building: a token-mode building deducts from the visible wallet, an
  // unlimited building (Facade Kit / Pro Plan, server `pack.is_unlimited`)
  // hides token numerics. Mirrors the render effort token hint.
  const { data: projectResponse } = useProject(projectId);
  const showsTokenCost =
    projectResponse?.data?.attributes?.pack?.is_unlimited === false;

  // Funnel-priority gate: until the concept's first design exists, Generate is
  // disabled with a "generate a design first" hint — a brand-new user must
  // never meet a paywall before the free design step. Once a design exists,
  // entitlement stays server-driven: no client-side plan/balance preflight,
  // the server 422s with an inline paywall payload and `use-album.ts` routes
  // it to the shared LimitPaywallDialog (Apple/Android parity).
  const hasCompletedMainRender = useHasCompletedMainRender(conceptId);
  const handleGenerateAlbum = (forceRegenerate = false) => {
    if (isArchived || isAlbumGenerating || !hasCompletedMainRender) return;
    const selectedRenderIds = Object.values(selectedRendersForAlbum);

    generateAlbum({
      // Template is the design axis. Send the actually selected template —
      // Pro holders can pick architect/studio; the server falls back to
      // standard for any not-yet-built design. Blueprints are an independent
      // free toggle that replaces the old full/short split.
      template: selectedTemplate,
      // Content-section flags — always sent (all default ON) so the server
      // records the snapshot explicitly. `include_blueprints` gates ONLY the
      // facade projections (blueprint drawings).
      include_blueprints: includeBlueprints,
      include_materials: includeMaterials,
      include_preparation_guide: includePreparationGuide,
      include_safety_requirements: includeSafetyRequirements,
      include_building_regulations: includeBuildingRegulations,
      language: storedSelectedLanguage,
      // Measurement system: sent only when the user explicitly picked Metric
      // or Imperial — "Auto" omits the field so the server decides.
      ...(measurementSystem !== 'auto'
        ? { measurement_system: measurementSystem }
        : {}),
      selected_renders: selectedRenderIds,
      // Estimate choice: explicit estimate_id (may be a sibling design's) or
      // generate_new_estimate — see useAlbumEstimatePicker.estimatePayload.
      ...estimatePicker.estimatePayload,
      ...(storedRequirements ? { requirements: storedRequirements } : {}),
      ...(forceRegenerate ? { force_regenerate: true } : {}),
    });
  };

  // Content-section toggle row — free Switch row with a trailing divider.
  // Rendered in two slices (Materials first, the rest after the Estimate
  // row), so the markup lives in one place.
  const renderSectionToggleRow = ({
    key,
    icon: Icon,
    labelKey,
  }: AlbumSectionToggleRow) => (
    <div key={key}>
      <SettingsItem
        icon={Icon}
        title={tAlbum(`settings.${labelKey}.title`)}
        disabled={isAlbumGenerating}
        toggle={{
          checked: albumSettings[key],
          onCheckedChange: (checked) => updateSetting(key, checked),
        }}
      />
      <div className="border-t border-border/30 mx-5" />
    </div>
  );

  // ---------- Detail header (shared by language/requirements views) ----------
  const detailHeader = (title: string) => (
    <div className="px-2 py-2 flex-shrink-0 flex items-center gap-2">
      <div className="flex items-center justify-start min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setView('main')}
          aria-label={tCommon('back')}
          className="inline-flex items-center justify-center size-9 rounded-md text-primary hover:text-primary/80 transition-colors"
        >
          <ChevronLeft className="size-5 rtl:-scale-x-100" />
        </button>
      </div>
      <span className="text-subpanel-title text-foreground truncate">
        {title}
      </span>
      <div className="flex-1" />
    </div>
  );

  // ---------- Footer (sticky Generate button — always visible) ----------
  const footer = (
    <div className="px-4 py-3 flex flex-shrink-0 flex-col items-center gap-2 border-t border-border/40 bg-muted">
      <Button
        variant={useSecondaryVariant ? 'secondary' : 'default'}
        onClick={() => handleGenerateAlbum()}
        size="cta"
        // Disabled until the first design exists — see handleGenerateAlbum.
        disabled={isArchived || isAlbumGenerating || !hasCompletedMainRender}
      >
        {isAlbumGenerating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {tAlbum('generatingAlbum')}
          </>
        ) : (
          <>
            {tAlbum('generateAlbum')}
            {/* Server-predicted token cost — suffix only when the server
                flags the cost as significant (threshold lives server-side;
                loading/error/below-threshold → plain label, no price) and
                only for legacy users (P4.3). */}
            {showsTokenCost && predictedTokens !== undefined && isSignificantCost && (
              <span className="inline-flex items-center gap-1">
                · ≈{predictedTokens}
                <WalnutIcon className="h-3.5 w-[11px] shrink-0" />
              </span>
            )}
          </>
        )}
      </Button>
      {/* Why-disabled hint: the free design step comes first (funnel rule). */}
      {!hasCompletedMainRender && !isArchived && (
        <span className="text-card-meta text-muted-foreground text-center">
          {tCommon('generateDesignFirst')}
        </span>
      )}
      {/* Unobtrusive "used/limit" meter for unlimited buildings — server-gated
          to appear only from two-thirds spent. */}
      <GenerationUsageMeter kind="albums" />
    </div>
  );

  // ---------- Main settings view ----------
  if (view === 'main') {
    return (
      <>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Renders that will be packed into the album — shown first so the
              selection is visible before any setting. */}
          {selectedRenderUrls.length > 0 && (
            <div className="px-4 pt-3">
              <AlbumRenderThumbnailStrip urls={selectedRenderUrls} />
            </div>
          )}

          {/* Partial renders warning */}
          {hasPartialRenders && (
            <div className="px-4 pt-2">
              <NoticeBanner
                variant="info"
                message={tAlbum('partialRendersWarning', {
                  renderedCount,
                  totalCount,
                })}
              />
            </div>
          )}

          {/* Album settings - grouped cards */}
          <div className="px-4 space-y-3 py-3">
            {/* Single soft ~80% pack-usage warning (albums) — server-driven
                `low` band on the building's pack, no counters (pivot rule).
                Renders nothing off the `low` band. */}
            <PackUsageNotice kind="albums" />

            <SettingsCard title={tAlbum('sections.settings')}>
              {/* Template Row — the DESIGN axis. Opens the template drill-down
                  view; the option list (free `standard`, Pro-tier
                  `architect`/`studio`) lives there. Mirrors Apple/Android and
                  the single-column AlbumPanelSettings. */}
              <SettingsItem
                icon={FileText}
                title={tAlbum('settings.type.label')}
                disabled={isAlbumGenerating}
                onClick={() => setView('template')}
                value={tAlbum(`settings.type.${selectedTemplate}`)}
              />

              <div className="border-t border-border/30 mx-5" />

              {/* Language Row */}
              <SettingsItem
                icon={Globe}
                title={tAlbum('settings.language')}
                disabled={isAlbumGenerating}
                onClick={openLanguageView}
                value={selectedLanguageName}
              />

              <div className="border-t border-border/30 mx-5" />

              {/* Content-section toggles — materials / blueprints /
                  preparation guide / safety requirements / building
                  regulations. Free Switch rows, all default ON;
                  `include_blueprints` gates ONLY the facade projections.
                  The Estimate row sits right after Materials — order
                  mirrors Apple/Android. */}
              {renderSectionToggleRow(MATERIALS_SECTION_TOGGLE_ROW)}

              {/* Estimate Row (FE4) — defaults to this design's latest ready
                  estimate; the drill-down also offers "Don't include", "New
                  estimate" and sibling designs' estimates. Always opens the
                  picker — the server returns the album paywall on Generate for
                  non-pack users. */}
              <SettingsItem
                icon={Banknote}
                title={tEstimate('album.label')}
                disabled={isAlbumGenerating}
                onClick={() => setView('estimate')}
                value={estimateRowValue}
              />

              <div className="border-t border-border/30 mx-5" />

              {SECONDARY_SECTION_TOGGLE_ROWS.map(renderSectionToggleRow)}

              {/* Requirements — shapes WHAT the album contains (alongside
                  Template/Language/Blueprints). Always opens the requirements
                  editor. Mirrors Apple/Android. */}
              <SettingsItem
                icon={ClipboardList}
                title={tAlbum('settings.requirements.label')}
                onClick={() => setView('requirements')}
                value={
                  storedRequirements
                    ? `${storedRequirements.substring(0, 40)}${storedRequirements.length > 40 ? '...' : ''}`
                    : undefined
                }
              />

              <div className="border-t border-border/30 mx-5" />

              {/* Units Row — measurement system for the album content (Auto /
                  Metric / Imperial). Free for everyone; Auto (default) lets
                  the server pick and omits the field from the generate
                  request. Last Settings-group row — mirrors Apple/Android. */}
              <SettingsItem
                icon={PencilRuler}
                title={tAlbum('settings.units.label')}
                disabled={isAlbumGenerating}
                onClick={() => setView('units')}
                value={tAlbum(`settings.units.${measurementSystem}`)}
              />
            </SettingsCard>

            {/* Significant-cost warning — server-computed flag + server-
                localized text (parity with Apple): shown right before the
                Generate button in the sticky footer below, only when the
                current settings make generation token-expensive. Token-worded
                — legacy users only (P4.3). */}
            {showsTokenCost && costWarningMessage != null && !isAlbumGenerating && (
              <NoticeBanner variant="warning" message={costWarningMessage} />
            )}
          </div>
        </div>
        {footer}
        {/* Duplicate album confirmation dialog (shared component) */}
        <AlbumDuplicateDialog
          open={duplicatePrompt.open}
          onOpenChange={duplicatePrompt.setOpen}
          generatedAt={duplicatePrompt.generatedAt}
          onUseExisting={duplicatePrompt.dismiss}
          onGenerateNew={() => {
            duplicatePrompt.dismiss();
            handleGenerateAlbum(true);
          }}
        />
      </>
    );
  }

  // ---------- Template detail view ----------
  if (view === 'template') {
    return (
      <>
        {detailHeader(tAlbum('settings.type.label'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          {/* Template = design axis. Only `standard` is selectable;
              `architect`/`studio` are "Coming soon" — disabled, no paywall on
              tap (product decision post-pivot). Mirrors Apple/Android. */}
          <div className="space-y-2">
            {ALBUM_TEMPLATE_OPTIONS.map((option) => {
              const isComingSoon = isPremiumAlbumTemplate(option);
              const isSelected = !isComingSoon && selectedTemplate === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    // "Coming soon" templates are inert — never select, never
                    // paywall. Mirrors Apple/Android.
                    if (isComingSoon) return;
                    updateSetting('selectedTemplate', option);
                    setView('main');
                  }}
                  disabled={isAlbumGenerating || isComingSoon}
                  className={cn(
                    'w-full flex flex-col gap-1 p-4 rounded-lg border-2 transition-colors text-start',
                    isSelected ? 'border-primary bg-primary/5' : 'border-border',
                    isComingSoon ? 'opacity-50' : 'hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <span className="text-list-item-subtitle">
                      {tAlbum(`settings.type.${option}`)}
                    </span>
                    {/* Quiet "Coming soon" marker on the unreleased templates —
                        not a Pass badge, no paywall. */}
                    {isComingSoon && (
                      <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide leading-none text-muted-foreground whitespace-nowrap shrink-0">
                        {tAlbum('settings.type.comingSoon')}
                      </span>
                    )}
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                  {option === 'standard' && (
                    <span className="text-card-meta text-muted-foreground">
                      {tAlbum('settings.type.standardDescription')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Language detail view ----------
  if (view === 'language') {
    return (
      <>
        {detailHeader(tAlbum('settings.language'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          <div className="space-y-2">
            {languageOrder.map((lang) => {
              const isSelected = lang.code === storedSelectedLanguage;
              return (
                <button
                  key={lang.code}
                  onClick={() => {
                    updateSetting(
                      'selectedLanguage',
                      lang.code as AlbumLanguage
                    );
                    setView('main');
                  }}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-start',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  )}
                >
                  <span className="text-list-item-subtitle">
                    {lang.name} ({lang.nativeName})
                  </span>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Estimate picker detail view (FE4) ----------
  if (view === 'estimate') {
    return (
      <>
        {detailHeader(tEstimate('album.label'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          <AlbumEstimateOptions
            currentDesignEstimates={currentDesignEstimates}
            otherDesignEstimates={otherDesignEstimates}
            selectedEstimateId={selectedEstimateId}
            isEstimateIncluded={isEstimateIncluded}
            onSelect={(id) => {
              setSelectedEstimateId(id);
              setView('main');
            }}
            onExclude={() => {
              excludeEstimate();
              setView('main');
            }}
            disabled={isAlbumGenerating}
          />
        </div>
        {footer}
      </>
    );
  }

  // ---------- Units detail view ----------
  if (view === 'units') {
    return (
      <>
        {detailHeader(tAlbum('settings.units.label'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          <div className="space-y-2">
            {ALBUM_MEASUREMENT_SYSTEM_OPTIONS.map((option) => {
              const isSelected = measurementSystem === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    updateSetting('measurementSystem', option);
                    setView('main');
                  }}
                  disabled={isAlbumGenerating}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-start',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  )}
                >
                  <span className="text-list-item-subtitle">
                    {tAlbum(`settings.units.${option}`)}
                  </span>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Requirements detail view ----------
  return (
    <>
      {detailHeader(tAlbum('settings.requirements.label'))}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
        <Textarea
          value={storedRequirements}
          onChange={(e) => updateSetting('requirements', e.target.value)}
          placeholder={tAlbum('settings.requirements.placeholder')}
          className="min-h-[200px] resize-none"
          maxLength={2000}
          disabled={isAlbumGenerating}
        />
      </div>
      {footer}
    </>
  );
}
